import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

class ApiService {
  private instance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  constructor() {
    this.instance = axios.create({
      baseURL: API_URL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private getStore() {
    // Lazy load store to avoid circular dependency
    const { store } = require('../store');
    return store;
  }

  private setupInterceptors() {
    this.instance.interceptors.request.use(
      (config) => {
        const store = this.getStore();
        const token = store.getState().auth.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add company ID header if available
        const currentCompany = store.getState().companies?.currentCompany;
        if (currentCompany?._id) {
          config.headers['x-company-id'] = currentCompany._id;
        }

        // Add cache-busting parameter to GET requests
        if (config.method === 'get') {
          config.params = {
            ...config.params,
            _t: Date.now()
          };
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't handle 401 errors for auth endpoints (login, register, etc.)
        const isAuthEndpoint = originalRequest.url?.includes('/auth/');
        
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(() => {
              return this.instance(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          const store = this.getStore();
          const { clearCredentials, refreshTokenAsync } = require('../store/slices/authSlice');
          const { addToast } = require('../store/slices/uiSlice');
          const refreshToken = store.getState().auth.refreshToken;

          if (refreshToken) {
            try {
              await store.dispatch(refreshTokenAsync(refreshToken));
              this.processQueue(null);
              return this.instance(originalRequest);
            } catch (refreshError) {
              this.processQueue(refreshError);
              store.dispatch(clearCredentials());
              store.dispatch(addToast({
                type: 'error',
                title: 'Session Expired',
                message: 'Please log in again to continue.',
              }));
              window.location.href = '/login';
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          } else {
            store.dispatch(clearCredentials());
            window.location.href = '/login';
            return Promise.reject(error);
          }
        }

        if (error.response?.status >= 500) {
          const store = this.getStore();
          const { addToast } = require('../store/slices/uiSlice');
          store.dispatch(addToast({
            type: 'error',
            title: 'Server Error',
            message: 'Something went wrong. Please try again later.',
          }));
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: any) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });

    this.failedQueue = [];
  }

  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.get(url, config);
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.post(url, data, config);
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.put(url, data, config);
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.patch(url, data, config);
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.delete(url, config);
  }

  public async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.instance.get(url, {
      ...config,
      responseType: 'blob'
    });
    return response.data;
  }

  public setAuthToken(token: string) {
    this.instance.defaults.headers.Authorization = `Bearer ${token}`;
  }

  public removeAuthToken() {
    delete this.instance.defaults.headers.Authorization;
  }

  public getApiUrl() {
    return API_URL;
  }
}

export const apiService = new ApiService();

export const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();

  const appendFormData = (obj: any, parentKey = '') => {
    if (obj === null || obj === undefined) return;

    if (obj instanceof File) {
      formData.append(parentKey, obj);
    } else if (obj instanceof FileList) {
      Array.from(obj).forEach((file, index) => {
        formData.append(`${parentKey}[${index}]`, file);
      });
    } else if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        const key = parentKey ? `${parentKey}[${index}]` : `${index}`;
        appendFormData(item, key);
      });
    } else if (typeof obj === 'object' && obj !== null) {
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        const newKey = parentKey ? `${parentKey}[${key}]` : key;
        appendFormData(value, newKey);
      });
    } else {
      formData.append(parentKey, String(obj));
    }
  };

  appendFormData(data);
  return formData;
};

export const downloadFile = async (url: string, filename: string) => {
  try {
    const response = await apiService.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('File download failed:', error);
    throw error;
  }
};

export const uploadFile = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<{ url: string; filename: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiService.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });

  return response.data;
};

export const isApiError = (error: any): boolean => {
  return error?.response?.data?.error || error?.message;
};

export const getApiErrorMessage = (error: any): string => {
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export default apiService;